<template>
  <Modal
    title="Designer einladen"
    class-name="large"
    :close-icon="closeIcon"
    :is-loading="isLoading"
    :is-visible="true"
  >
    <template v-slot:body>
      <div v-if="!invitesSendSuccess">
        <p class="text-black">
          <span v-html="designerCountMessage"></span>
          <span
            v-if="invite.BasePaymentCount > 0"
            v-html="basePaymentCountMessage"
          >
          </span>
        </p>
        <Callout
          v-if="isTooManyParticipationPayments"
          type="warning"
          :icon-url="warningIcon"
          title="Zu viele Beteiligungszahlungen zugewiesen"
          :closable="true"
          @close="isTooManyParticipationPayments = false"
        >
          <template v-slot:message>
            Sie haben nur {{ invite.BasePaymentCount }} Teilnahmezahlung(en) zuzuweisen. Deaktivieren Sie die Teilnahmezahlung eines anderen Designers, bevor Sie eine Zuweisung versuchen
          </template>
        </Callout>
        <div v-if="inviteData" class="table-scroll-modal">
          <table class="table stack">
            <thead class="hide-for-small-only hide-for-medium-only">
              <tr>
                <th>Designer</th>
                <th>Einladung versenden?</th>
                <th v-if="invite.BasePaymentCount > 0">
                  Teilnahme-Zahlung senden?
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(designer, index) in inviteData.Designers"
                :key="index"
              >
                <td>
                  <div class="user-profile user-profile--tiny mhn">
                    <div class="row collapse align-middle mhn">
                      <div class="column shrink">
                        <div
                          v-if="designer.ProfilePicUrl"
                          class="user-profile__image"
                        >
                          <div class="responsive-embed square">
                            <img
                              class="responsive-embed__image"
                              height="40"
                              width="40"
                              :src="designer.ProfilePicUrl"
                              alt="Profile picture"
                            />
                          </div>
                        </div>
                      </div>
                      <div class="column">
                        <div class="row">
                          <div class="column small-12">
                            <span class="user-profile__small-text">
                              <a :href="designer.PortfolioUrl">
                                {{ designer.DisplayName }}
                              </a>
                            </span>
                          </div>
                          <div
                            v-if="designer.CountryName"
                            class="column small-12"
                          >
                            <span class="user-profile__small-text">
                              {{ designer.CountryName }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <Checkbox
                    :id="`invite-designer-${designer.DesignerId}`"
                    :checked="invitedDesigners[index].isInvited"
                    @input="onInviteDesignerChecked($event, index)"
                  >
                    Einladung zum Projekt
                  </Checkbox>
                </td>
                <td v-if="invite.BasePaymentCount">
                  <Checkbox
                    :id="`send-participation-payment-${designer.DesignerId}`"
                    :checked="invitedDesigners[index].isPaidInvite"
                    @input="onParticipationPaymentChecked($event, index)"
                  >
                    Beteiligungsentgelte
                  </Checkbox>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="text-center">
          <Checkbox id="reallocate-invites" @input="reallocateInvites = $event">
            Neuverteilung der Einladungen, wenn abgelehnt
          </Checkbox>
        </div>
      </div>
      <div v-else>
        <Callout
          type="success"
          :icon-url="successIcon"
          title="Einladung erfolgreich gesendet"
          @close="isTooManyParticipationPayments = false"
        >
          <template v-slot:message>
            Wir haben {{ invitedDesignersCount }} Einladung(en) für Sie gesendet.
          </template>
        </Callout>
        <p>
          Sie können auch Designer in unseren Designerverzeichnisse auswählen und einladen. Um so zu tun, einfach:
        </p>
        <ol>
          <li>
            <span v-html="designerPortfolioMessage"></span>
          </li>
          <li>
            Finden Sie einen Designer, den Sie persönlich zu Ihrem Projekt einladen wollen
          </li>
          <li>
            <span v-html="getADesignFromMeMessage"></span>
            <img
              :src="inviteDesignerPortfolioBanner"
              alt="Invite Designer Portfolio Banner"
            />
          </li>
          <li>
            <span v-html="forMoreInfoMessage"></span>
          </li>
        </ol>
      </div>
    </template>
    <template v-slot:footer>
      <div v-if="!invitesSendSuccess">
        <input
          type="submit"
          :class="{
            ['button small mrm']: true,
            disabled: invitedDesignersCount === 0,
          }"
          value="Einladungen senden"
          @click="onClickSubmitInvites"
        />
        <button
          class="button secondary hollow small"
          data-close
          data-role="cancel-invite-wizard"
        >
          Stornieren
        </button>
      </div>
      <div v-else>
        <button class="button small" data-close>Okay, verstanden</button>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from '../../../shared/Modal/Modal.vue';
import Checkbox from '../../../shared/Checkbox/Checkbox.vue';
import Callout from '../../../shared/Callout/Callout.vue';
import submitInvitesAsync from '../../../../clients/invites-api';

export default {
  components: {
    Modal,
    Checkbox,
    Callout,
  },
  props: {
    warningIcon: {
      type: String,
      default: undefined,
    },
    successIcon: {
      type: String,
      default: undefined,
    },
    invite: {
      type: Object,
      required: true,
    },
    closeIcon: {
      type: String,
      default: undefined,
    },
    inviteDesignerPortfolioBanner: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      invitedDesigners: [],
      inviteData: null,
      isTooManyParticipationPayments: false,
      reallocateInvites: false,
      invitesSendSuccess: false,
      isLoading: false,
    };
  },
  computed: {
    designerCount() {
      return this.invite.Designers.length;
    },
    invitedDesignersCount() {
      return this.invitedDesigners.filter((payment) => payment.isInvited)
        .length;
    },
    briefId() {
      const urlMatch = this.$route
        ? this.$route.fullPath.match(/([^/]+$)/)
        : null;

      if (urlMatch && urlMatch.length > 1) {
        return parseInt(urlMatch[1], 10);
      }

      return null;
    },
    designerCountMessage() {
      return `Wir empfehlen die Einladung von <strong>${this.designerCount} Designern</strong> zu Ihrem Projekt.`;
    },
    basePaymentCountMessage() {
      return `Sie können <strong>${this.invite.BasePaymentCount} Beteiligungszahlungen (bezahlte Einladungen) aufteilen,  um Top Designer zu locken, auf Ihr Projekt zu arbeiten</strong>.`;
    },
    getADesignFromMeMessage() {
      return `Klicken Sie auf das <strong>Erhalten Sie ein Design von mir</strong> auf einem Designer-Portfolio und folgen Sie den Anweisungen`;
    },
    forMoreInfoMessage() {
      return `Für weitere Informationen siehe <a href='${this.invite.ForMoreInfoUrl}'>Kann ich Designer zu meinem Projekt einladen?</a>`;
    },
    designerPortfolioMessage() {
      return `Gehen Sie auf das Designer-Portfolio - klicken Sie auf den Link des <a href='${this.invite.DesignersListUrl}'>Designers</a> in der Navigationsleiste von DesignCrowd`;
    },
  },
  mounted() {
    this.initialiseInvitedDesigners();
    this.inviteData = this.invite;
  },
  methods: {
    initialiseInvitedDesigners() {
      this.invitedDesigners = this.invite.Designers.map((designer) => ({
        designerId: designer.DesignerId,
        isPaidInvite: designer.RecommendedForPaidInvite,
        isInvited: true,
      }));
    },
    onInviteDesignerChecked(checked, index) {
      this.$set(this.invitedDesigners[index], 'isInvited', checked);
    },
    onParticipationPaymentChecked(checked, index) {
      this.$set(this.invitedDesigners[index], 'isPaidInvite', checked);
      const checkedPayments = this.invitedDesigners.filter(
        (x) => x.isPaidInvite,
      );

      if (
        checkedPayments &&
        checkedPayments.length > this.inviteData.BasePaymentCount
      ) {
        this.$nextTick(() => {
          this.$set(this.invitedDesigners[index], 'isPaidInvite', false);
          this.isTooManyParticipationPayments = true;
        });
      } else {
        this.isTooManyParticipationPayments = false;
      }
    },
    async onClickSubmitInvites() {
      if (this.invitedDesignersCount > 0) {
        const invites = {
          autoReallocate: this.reallocateInvites,
          designers: this.invitedDesigners.filter(
            (designer) => designer.isInvited,
          ),
        };

        this.isLoading = true;
        const res = await submitInvitesAsync(this.briefId, invites);
        this.invitesSendSuccess = res;
        this.isLoading = false;
      }
    },
  },
};
</script>
